import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select, Form, Upload, Image, Tag, Checkbox, DatePicker } from "antd";
import moment from "moment";
import toast from 'react-hot-toast';
import { SendMarketingEmailApi } from '../../../api/request/emailMarketing';
import LoadableButton from '../../../components/buttons/LoadableButton';
import HTMLEditor from '../../../components/global/htmlEditor/HTMLEditor';
import { GetAllUsersApi } from '../../../api/request/settings/notifications';
import FilterIcon from '../../../assets/FilterIcon.jsx';
import { CloseCircleOutlined } from '@ant-design/icons';
const AddEmail = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [fileList, setFileList] = useState([]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [allUsers, setAllUsers] = useState([])
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [userTypeFilter, setUserTypeFilter] = useState('all');
    const [kycStatusFilter, setKycStatusFilter] = useState('all');
    const [ftdStatusFilter, setFtdStatusFilter] = useState('all');
    const [isOptionsLoading, setIsOptionsLoading] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const emailEditorRef = useRef(null);

    const filteredOptions = allUsers?.filter(option => !selectedUsers.map(item => item.value).includes(option.value));

    const userTypeOptions = [
        { label: 'All', value: 'all' },
        { label: 'IB', value: 'ib' },
        { label: 'User', value: 'user' }
    ];

    const kycOptions = [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Completed', value: 'completed' }
    ];

    const ftdOptions = [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Completed', value: 'completed' }
    ];


    const getBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
        form.setFieldsValue({ UploadImage: newFileList });
        if (newFileList?.length == 0) {
            form.setFieldsValue({ UploadImage: [] });
        }
    }

    const handleChangeOption = (values) => {
        try {
            const newSelectedUsers = values.map(value => allUsers.find(option => option.value === value));
            setSelectedUsers(newSelectedUsers);

            // Update select all checkbox state
            setSelectAll(newSelectedUsers.length === allUsers.length && allUsers.length > 0);
        } catch (error) {
            toast.error('Error selecting users');
            console.error('Selection error:', error);
        }
    };

    const handleSelectAll = (e) => {
        const checked = e.target.checked;
        setSelectAll(checked);

        if (checked) {
            // Select all users
            setSelectedUsers([...allUsers]);
            form.setFieldsValue({ userId: allUsers.map(user => user.value) });
            toast.success(`Selected ${allUsers.length} users successfully`);
        } else {
            // Deselect all users
            setSelectedUsers([]);
            form.setFieldsValue({ userId: [] });
        }
    };

    const filterOption = (input, option) => {
        return (option?.labelText ?? "").toLowerCase().includes(input.toLowerCase());
    };

    const tagRender = (props) => {
        const { value, closable, onClose } = props;
        const option = allUsers.find(opt => opt.value === value);
        return (
            <span className="inline-flex items-center px-2 py-[1px] rounded-sm mr-1 my-1 bg-primary-light">
                {option?.label}
                {closable && (<span onClick={onClose} className="cursor-pointer ml-2">x</span>)}
            </span>
        );
    };

    const clearAllFilters = () => {
        const filters = {
            userType: 'all',
            kycStatus: 'all',
            ftdStatus: 'all'
        };

        setUserTypeFilter(filters.userType);
        setKycStatusFilter(filters.kycStatus);
        setFtdStatusFilter(filters.ftdStatus);

        fetchUsers('', filters);
    };

    const hasActiveFilters = userTypeFilter !== 'all' || kycStatusFilter !== 'all' || ftdStatusFilter !== 'all';
    const handleApplyFilters = () => { fetchUsers(); };

    const fetchUsers = async (query = '', overrideFilters = null) => {
        setIsOptionsLoading(true);

        const appliedUserType = overrideFilters?.userType ?? userTypeFilter;
        const appliedKyc = overrideFilters?.kycStatus ?? kycStatusFilter;
        const appliedFtd = overrideFilters?.ftdStatus ?? ftdStatusFilter;

        const userTypeParam =
            appliedUserType === "ib" ? "IB" :
                appliedUserType === "user" ? "User" : '';

        const kycParam =
            appliedKyc === 'pending' ? ['No'] :
                appliedKyc === 'completed' ? ['Yes'] : [];

        const ftdParam =
            appliedFtd === 'pending' ? ['No'] :
                appliedFtd === 'completed' ? ['Yes'] : [];

        const params = {
            ApplyPagination: false,
            UserType: userTypeParam,
            KYCType: kycParam,
            FTDType: ftdParam,
            SearchText: query
        };

        try {
            const { data } = await GetAllUsersApi(new URLSearchParams(params).toString());

            if (data?.success) {
                const fetched = data?.data?.map((item) => ({
                    value: item?.userId,
                    label: `${item?.firstName} ${item?.lastName}`,
                    email: item?.email
                }));

                const ensureSelected = selectedUsers.filter(
                    sel => !fetched.some(opt => opt.value === sel.value)
                );

                const newAllUsers = [...(fetched || []), ...ensureSelected];
                setAllUsers(newAllUsers);

                setSelectAll(
                    selectedUsers.length === newAllUsers.length &&
                    newAllUsers.length > 0
                );
            }
        } catch (err) {
            console.error('Error fetching users', err);
        } finally {
            setIsOptionsLoading(false);
        }
    };

    const UploadButton = (
        <button type="button">
            <div className="flex justify-center"><img src="/assets/icons/Upload_Document_icon.svg" alt="icon" /></div>
            Upload
        </button>
    );

    const getHtml = async (values) => {
        setIsLoading(true)
        await emailEditorRef?.current?.editor?.exportHtml((data) => {
            const { html } = data;
            handleSubmit(values, html)
        });
    }

    const toUtcIsoString = (dateValue) => {
        if (!dateValue) return null;

        // Works for both dayjs and moment objects.
        if (typeof dateValue?.valueOf === "function") {
            const timestamp = dateValue.valueOf();
            if (!Number.isNaN(timestamp)) {
                return new Date(timestamp).toISOString();
            }
        }

        const fallback = moment(dateValue);
        return fallback.isValid() ? fallback.toDate().toISOString() : null;
    };

    const handleSubmit = async (values, html) => {
        try {
            const formData = new FormData();
            formData.append("Subject", values?.Subject);
            const users = selectedUsers.map(item => item.value);
            users.map((item, index) => { formData.append(`UserId[${index}]`, item); })
            formData.append("Body", html);
            fileList.forEach((file) => { formData.append("Attachment", file.originFileObj); });
            if (values?.ScheduleTime) {
                const utcIsoString = toUtcIsoString(values.ScheduleTime);
                if (utcIsoString) {
                    formData.append("ScheduleTime", utcIsoString);
                }
            }
            const { data } = await SendMarketingEmailApi(formData)
            if (data?.success) {
                navigate("/email_marketing")
                toast.success(data?.message)
            }
            setIsLoading(false)
        }
        catch (error) {
            toast.error(error?.response?.data?.message)
            setIsLoading(false)
        }
    }

    function customDebounce(func, delay) {
        let timer;
        return function (...args) {
            clearTimeout(timer);
            timer = setTimeout(() => {
                func.apply(this, args);
            }, delay);
        };
    }

    const debouncedClientSearch = customDebounce((value, type) => {
        if (value.length >= 3) {
            if (type === "from") {
                fetchUsers(value);
            }
        }
    }, 500);

    const disabledScheduleDate = (current) => {
        if (!current) return false;
        const currentValue = typeof current?.valueOf === "function" ? current.valueOf() : new Date(current).getTime();
        return currentValue < moment().startOf("day").valueOf();
    };

    const disabledScheduleTime = (selectedDate) => {
        if (!selectedDate) return {};

        const selectedMoment = moment(typeof selectedDate?.valueOf === "function" ? selectedDate.valueOf() : selectedDate);
        const now = moment();

        if (!selectedMoment.isSame(now, "day")) {
            return {};
        }

        const disabledHours = Array.from({ length: now.hour() }, (_, i) => i);
        const disabledMinutes = (hour) =>
            hour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : [];
        const disabledSeconds = (hour, minute) =>
            hour === now.hour() && minute === now.minute()
                ? Array.from({ length: now.second() }, (_, i) => i)
                : [];

        return { disabledHours: () => disabledHours, disabledMinutes, disabledSeconds };
    };

    return (
        <div className='border-2 border-light rounded-xl p-4'>
            <Form className='w-full' autoComplete="off" form={form} onFinish={getHtml}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="border border-light rounded-lg p-4 min-h-[191px]">
                            <div className="flex items-center justify-between mb-3 ">
                                <h3 className="text-base font-semibold flex items-center gap-2 ">
                                    <FilterIcon active={hasActiveFilters} />
                                    Filter Recipients</h3>
                                <div className="flex items-center gap-2">
                                    {hasActiveFilters && (
                                        <button
                                            type="button"
                                            onClick={clearAllFilters}
                                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 transition-colors"
                                        >
                                            <CloseCircleOutlined />
                                            Clear All
                                        </button>
                                    )}

                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div className="flex flex-col gap-1 ant-select-selector-white">
                                    <label className="text-sm font-medium">User Type</label>
                                    <Select
                                        value={userTypeFilter}
                                        onChange={setUserTypeFilter}
                                        options={userTypeOptions}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 ant-select-selector-white">
                                    <label className="text-sm font-medium">KYC Status</label>
                                    <Select
                                        value={kycStatusFilter}
                                        onChange={setKycStatusFilter}
                                        options={kycOptions}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div className="flex flex-col gap-1 ant-select-selector-white">
                                    <label className="text-sm font-medium">FTD Status</label>
                                    <Select
                                        value={ftdStatusFilter}
                                        onChange={setFtdStatusFilter}
                                        options={ftdOptions}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-light">
                                <div className='w-full flex justify-between'>
                                    <div>
                                        <span className="text-sm text-gray-500">Active Filters:  </span>
                                        {hasActiveFilters ? (
                                            <>
                                                {userTypeFilter !== 'all' && (
                                                    <Tag closable onClose={() => setUserTypeFilter('all')} color="blue">
                                                        {userTypeOptions.find(o => o.value === userTypeFilter)?.label}
                                                    </Tag>
                                                )}
                                                {kycStatusFilter !== 'all' && (
                                                    <Tag closable onClose={() => setKycStatusFilter('all')} color="blue">
                                                        {kycOptions.find(o => o.value === kycStatusFilter)?.label}
                                                    </Tag>
                                                )}
                                                {ftdStatusFilter !== 'all' && (
                                                    <Tag closable onClose={() => setFtdStatusFilter('all')} color="blue">
                                                        {ftdOptions.find(o => o.value === ftdStatusFilter)?.label}
                                                    </Tag>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-sm text-gray-500"> </span>
                                        )}
                                    </div>
                                    <div>
                                        <LoadableButton
                                            lable="Apply"
                                            type="button"
                                            onClick={handleApplyFilters}
                                            isLoading={isOptionsLoading}
                                            loadingLable="Applying..."
                                            className="bg-primary text-sm text-white font-semibold rounded-lg px-8 py-1.5 uppercase themeHover duration-500"
                                        />
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="border border-light rounded-lg p-4 ">
                            <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                <div className='input-white ant-multi-select-selector'>
                                    <div className="flex items-center justify-between ">
                                        <label className='text-base font-medium'>Recipient Users</label>
                                        <Checkbox
                                            checked={selectAll}
                                            onChange={handleSelectAll}
                                            disabled={isOptionsLoading || allUsers.length === 0}
                                        >
                                            <span className="text-sm">
                                                Select All ({allUsers?.length})
                                            </span>
                                        </Checkbox>
                                    </div>
                                    <Form.Item
                                        name="userId"
                                        rules={[{ required: true, message: 'Please select user.' }]}
                                    >

                                        <Select
                                            loading={isOptionsLoading}
                                            mode="multiple"
                                            className='p-0 ant-select-selector-white mt-1'
                                            placeholder="Select users"
                                            value={selectedUsers.map(item => item.value)}
                                            onChange={handleChangeOption}
                                            // options={filteredOptions}
                                            options={filteredOptions.map((a) => ({
                                                value: a.value,
                                                label: (
                                                    <div className="flex gap-3 items-center text-sm">
                                                        <span className="font-medium">{a.label}</span>
                                                        <span className="text-xs text-gray-500">{a.email} </span>
                                                    </div>
                                                ),
                                                labelText: `${a.label} ${a.email}`,
                                            }))}
                                            tagRender={tagRender}
                                            filterOption={filterOption}
                                            showSearch
                                            notFoundContent={isOptionsLoading ? "Loading users..." : "No users found"}
                                            onSearch={(value) => {
                                                debouncedClientSearch(value, 'from');
                                            }}
                                        />
                                    </Form.Item>
                                </div>
                                <div className='input-white'>
                                    <label className='text-base font-medium mb-0.5'>Schedule Time (UTC)</label>
                                    <Form.Item name="ScheduleTime">
                                        <DatePicker
                                            showTime={{ format: 'HH:mm', use12Hours: false }}
                                            showNow={false}
                                            format="DD-MM-YYYY HH:mm"
                                            placeholder="Select date and time (saved as UTC)"
                                            className="mt-1 w-full ps-4"
                                            style={{ width: '100%' }}
                                            size="large"
                                            disabledDate={disabledScheduleDate}
                                            disabledTime={disabledScheduleTime}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <div className='input-white mt-5'>
                                <label className='text-base font-medium mb-0.5'>Subject</label>
                                <Form.Item
                                    name="Subject"
                                    rules={[
                                        { required: true, message: 'Please enter email subject.' },
                                        { max: 200, message: 'Subject cannot exceed 200 characters.' }
                                    ]}
                                >
                                    <Input
                                        placeholder="Email subject"
                                        className="mt-1 ps-4"
                                        maxLength={200}
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="mt-2">
                    <label className='text-base text-[#2F2B3DCC] font-medium mb-0.5'>Body</label>
                    <HTMLEditor emailEditorRef={emailEditorRef} />
                </div>

                <div className="mt-2">
                    <label className="text-base text-[#2F2B3DCC] font-medium">Attchment</label>
                    <Form.Item name="Upload Attchment">
                        <Upload
                            beforeUpload={() => false}
                            listType="picture-card"
                            className="mt-1"
                            fileList={fileList}
                            onPreview={handlePreview}
                            onChange={handleChange}
                        >
                            {fileList.length >= 1 ? null : UploadButton}
                        </Upload>
                    </Form.Item>
                    {previewImage && (
                        <Image
                            wrapperStyle={{ display: 'none' }}
                            preview={{
                                visible: previewOpen,
                                onVisibleChange: (visible) => setPreviewOpen(visible),
                                afterOpenChange: (visible) => !visible && setPreviewImage(''),
                            }}
                            src={previewImage}
                        />
                    )}
                </div>
                <div className='flex justify-end items-center gap-5 mt-6'>
                    <LoadableButton
                        className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover  duration-500'
                        disabled={isLoading}
                        type="Submit"
                        lable='Submit'
                        isLoading={isLoading}
                        loadingLable='Adding email...'
                    />
                </div>
            </Form>
        </div>
    )
}

export default AddEmail